/**
 * About - What is AI Expo section
 * Describes AI sectors and related projects
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../ComponentsStyling/whatIsAiExpo.css';

/* Utility functions */

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Counter component with animation */

const Counter = ({ target, duration = 1400 }) => {
    const ref = useRef(null);
    const [value, setValue] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (!e.isIntersecting) return;
                if (prefersReducedMotion()) {
                    setValue(target);
                    obs.unobserve(el);
                    return;
                }
                const start = performance.now();
                const tick = (now) => {
                    const p = Math.min((now - start) / duration, 1);
                    const eased = 1 - Math.pow(1 - p, 3);
                    setValue(Math.floor(target * eased));
                    if (p < 1) requestAnimationFrame(tick);
                    else setValue(target);
                };
                requestAnimationFrame(tick);
                obs.unobserve(el);
            });
        }, { threshold: 0.6 });
        obs.observe(el);
        return () => obs.disconnect();
    }, [target, duration]);

    return <span ref={ref}>{String(value).padStart(2, '0')}</span>;
};

/* Sectors data */

const SECTORS = [
    {
        index: '01',
        name: 'Education',
        body: 'AI that improves learning — personalized tutors, smart assessment, and tools that meet students where they are.',
        tags: ['Tutoring', 'Analytics', 'EdTech'],
        accent: 'azure',
    },
    {
        index: '02',
        name: 'Healthcare',
        body: 'AI applications supporting medical and health services — disease prediction, medical imaging, patient-monitoring systems.',
        tags: ['Diagnostics', 'Imaging', 'Monitoring'],
        accent: 'azure',
    },
    {
        index: '03',
        name: 'FinTech',
        body: 'AI in financial technology — fraud detection, forecasting, credit scoring, and the systems that move trust through markets.',
        tags: ['Fraud', 'Forecasting', 'Advisory'],
        accent: 'purple',
    },
    {
        index: '04',
        name: 'Robotics & Hardware',
        body: 'AI integrated with physical devices — robot navigation, embedded intelligence, sensor-driven automation.',
        tags: ['Embedded', 'Sensors', 'Automation'],
        accent: 'azure',
    },
    {
        index: '05',
        name: 'GovTech',
        body: 'AI solutions serving the public sector — e-government platforms, smart transportation, public safety systems, and urban infrastructure that improve how governments deliver services to citizens.',
        tags: ['Digital Governance', 'Smart Transportation', 'Public Safety'],
        accent: 'azure',
    },
];

/* -------------------------------------------------------------------------- */
/*  Mouse-follow spotlight (cheap, requestAnimationFrame-throttled)           */
/* -------------------------------------------------------------------------- */

const useSpotlight = (ref) => {
    useEffect(() => {
        const el = ref.current;
        if (!el || prefersReducedMotion()) return;

        let raf = 0;
        let pending = null;

        const onMove = (e) => {
            const rect = el.getBoundingClientRect();
            pending = {
                x: ((e.clientX - rect.left) / rect.width) * 100,
                y: ((e.clientY - rect.top) / rect.height) * 100,
            };
            if (!raf) {
                raf = requestAnimationFrame(() => {
                    if (pending) {
                        el.style.setProperty('--mx', pending.x + '%');
                        el.style.setProperty('--my', pending.y + '%');
                    }
                    raf = 0;
                });
            }
        };

        el.addEventListener('mousemove', onMove);
        return () => {
            el.removeEventListener('mousemove', onMove);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [ref]);
};

/* -------------------------------------------------------------------------- */
/*  About                                                                     */
/* -------------------------------------------------------------------------- */

const About = () => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useSpotlight(ref);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const obs = new IntersectionObserver((entries) => {
            entries.forEach(e => {
                if (e.isIntersecting) {
                    setVisible(true);
                    obs.unobserve(el);
                }
            });
        }, { threshold: 0.1 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className={`abt${visible ? ' is-visible' : ''}`}
            id="about"
        >
            {/* Background layers — grid + soft ambient glow following cursor */}
            <div className="abt__grid" aria-hidden />
            <div className="abt__spot" aria-hidden />
            <div className="abt__noise" aria-hidden />

            <div className="abt__wrap">

                {/* ───────── HUD STRIP ───────── */}
                <div className="abt__hud" aria-hidden>
                    <span className="abt__hud-tag">
                        <span className="abt__hud-dot" />
                        SEC&nbsp;//&nbsp;02
                    </span>
                    <span className="abt__hud-line" />
                    <span className="abt__hud-mid">ABOUT&nbsp;·&nbsp;AI&nbsp;EXPO&nbsp;JORDAN&nbsp;2026</span>
                    <span className="abt__hud-line" />
                    <span className="abt__hud-tag abt__hud-tag--right">
                        EDITION&nbsp;01
                        <span className="abt__hud-blink" />
                    </span>
                </div>

                {/* ───────── HEADER ───────── */}
                <header className="abt__head">
                    <div className="abt__eyebrow">
                        <span className="abt__eyebrow-bracket">[</span>
                        <span>What we are building</span>
                        <span className="abt__eyebrow-bracket">]</span>
                    </div>

                    <h2 className="abt__title">
                        <span className="abt__title-row">
                            <span className="abt__title-num" aria-hidden>01</span>
                            <span className="abt__title-text">Where Jordan&apos;s</span>
                        </span>
                        <span className="abt__title-row abt__title-row--alt">
                            <span className="abt__title-text abt__title-text--outline">builders</span>
                            <span className="abt__title-text">show up.</span>
                        </span>
                    </h2>

                    <div className="abt__lede-wrap">
                        <span className="abt__lede-bar" aria-hidden />
                        <p className="abt__lede">
                            AI Expo Jordan 2026 is a competition-based AI exhibition
                            organized by <b>IEEE CIS UJ</b> at the University of Jordan.
                            Students, graduation teams, and startups showcase real AI
                            projects through live demos, compete in front of a
                            professional judging panel, and connect with the wider tech
                            community — across two days, <b>1 &amp; 3 October 2026</b> at
                            <b> UJ Academy</b>.
                        </p>
                    </div>
                </header>

                {/* ───────── INTENT ───────── */}
                <section className="abt__intent">
                    <div className="abt__intent-label" aria-hidden>
                        <span className="abt__intent-corner abt__intent-corner--tl" />
                        <span className="abt__intent-corner abt__intent-corner--tr" />
                        <span className="abt__intent-corner abt__intent-corner--bl" />
                        <span className="abt__intent-corner abt__intent-corner--br" />
                        FOUNDING&nbsp;INTENT
                    </div>
                    <p className="abt__intent-text">
                        AI Expo Jordan exists to close the gap between academic AI
                        and industry application — to help projects move from the
                        university desk to a working stage. Our mission is to turn
                        AI ideas into real, working systems and to connect Jordan&apos;s
                        student innovators with the industry that takes them further.
                    </p>
                </section>

                {/* ───────── SECTORS ───────── */}
                <section className="abt__sectors">
                    <div className="abt__sectors-head">
                        <div className="abt__sectors-marker">
                            <span className="abt__sectors-marker-num">05</span>
                            <span className="abt__sectors-marker-label">Sectors</span>
                        </div>
                        <p className="abt__sectors-sub">
                            Every project at AI Expo Jordan belongs to one of five
                            focus sectors. Pick the one that fits the problem
                            you&apos;re solving — not the technology you&apos;re using.
                        </p>
                    </div>

                    <div className="abt__sectors-grid">
                        {SECTORS.map((s, i) => (
                            <article
                                key={s.index}
                                className={`abt-sector abt-sector--${s.accent}`}
                                style={{ '--i': i }}
                            >
                                {/* Corner brackets */}
                                <span className="abt-sector__corner abt-sector__corner--tl" />
                                <span className="abt-sector__corner abt-sector__corner--tr" />
                                <span className="abt-sector__corner abt-sector__corner--bl" />
                                <span className="abt-sector__corner abt-sector__corner--br" />

                                {/* Sweep gradient on hover */}
                                <span className="abt-sector__sweep" aria-hidden />

                                <div className="abt-sector__top">
                                    <span className="abt-sector__index">S/{s.index}</span>
                                    <span className="abt-sector__arrow" aria-hidden>
                                        <svg viewBox="0 0 16 16" fill="none">
                                            <path d="M3 13L13 3M13 3H6M13 3v7"
                                                stroke="currentColor"
                                                strokeWidth="1.4"
                                                strokeLinecap="square" />
                                        </svg>
                                    </span>
                                </div>

                                <h3 className="abt-sector__name">{s.name}</h3>

                                <p className="abt-sector__body">{s.body}</p>

                                <div className="abt-sector__tags">
                                    {s.tags.map((t, j) => (
                                        <span key={j} className="abt-sector__tag">
                                            <span className="abt-sector__tag-dot" />
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </article>
                        ))}
                    </div>
                </section>

                {/* ───────── METRICS ───────── */}
                <section className="abt__metrics">
                    <div className="abt__metrics-head">
                        <span className="abt__metrics-tick" />
                        BY&nbsp;THE&nbsp;NUMBERS
                        <span className="abt__metrics-divider">{"//"}</span>
                        <b>EDITION&nbsp;01</b>
                    </div>

                    <div className="abt__metrics-grid">
                        <div className="abt-metric">
                            <span className="abt-metric__value"><Counter target={3} /></span>
                            <span className="abt-metric__label">Tracks</span>
                            <span className="abt-metric__caption">Students · Graduation · Startups</span>
                        </div>
                        <div className="abt-metric">
                            <span className="abt-metric__value"><Counter target={5} /></span>
                            <span className="abt-metric__label">Sectors</span>
                            <span className="abt-metric__caption">Edu · Health · Fin · Robotics · Gov</span>
                        </div>
                        <div className="abt-metric">
                            <span className="abt-metric__value"><Counter target={3} /></span>
                            <span className="abt-metric__label">Workshops</span>
                            <span className="abt-metric__caption">Free · 9, 16, 23 May</span>
                        </div>
                        <div className="abt-metric abt-metric--accent">
                            <span className="abt-metric__value"><Counter target={2} /></span>
                            <span className="abt-metric__label">Days</span>
                            <span className="abt-metric__caption">1 &amp; 3 Oct 2026 · UJ Academy</span>
                        </div>
                    </div>
                </section>

                {/* ───────── QUOTE ───────── */}
                <section className="abt__quote">
                    <div className="abt__quote-glass" aria-hidden />
                    <span className="abt__quote-mark" aria-hidden>“</span>
                    <p className="abt__quote-text">
                        A Jordan where practical AI development starts in universities and finds its way into the real world — where students and startups build things that actually matter, and where the distance between academic AI and industry application keeps getting smaller. 
                    </p>
                    <div className="abt__quote-by">
                        <span className="abt__quote-rule" />
                        <span>IEEE&nbsp;CIS&nbsp;UJ&nbsp;·&nbsp;<b>University of Jordan</b></span>
                    </div>
                </section>

                {/* ───────── CTA ───────── */}
                <footer className="abt__close">
                    <Link to="/about" className="abt__cta">
                        <span className="abt__cta-bracket">[</span>
                        <span className="abt__cta-text">About AI Expo</span>
                        <span className="abt__cta-arrow">
                            <svg viewBox="0 0 24 12" fill="none" aria-hidden>
                                <path
                                    d="M1 6h22m0 0L18 1m5 5l-5 5"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="square"
                                />
                            </svg>
                        </span>
                        <span className="abt__cta-bracket">]</span>
                    </Link>
                </footer>

            </div>
        </section>
    );
};

export default About;